import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { Box, Tooltip, Typography } from "@mui/material";
import type { AnalyzedMoveResult } from "@/shared/api/engine.api";
import { formatEvaluation, type CriticalMoment } from "@chessla/shared";
import { StatusBadge } from "@/shared/components/StatusBadge";
import { titleCase } from "../review-format";
import { motifDescription, motifLabel, tacticalDescription } from "../review-copy";
import { formatTimeSpent } from "@/shared/utils/chess";
import { ReviewDataPoint } from "./ReviewDataPoint";
import { ReviewSectionTitle } from "./ReviewSectionTitle";
import { PrincipleSignalBadge } from "./PrincipleSignalBadge";

export const SelectedMovePanel = ({
  move,
  moment,
  revealEvidence = true,
  revealCarry = true,
  showOpponentEvidence = false,
}: {
  move: AnalyzedMoveResult | null;
  moment?: CriticalMoment | null;
  revealEvidence?: boolean;
  revealCarry?: boolean;
  showOpponentEvidence?: boolean;
}) => (
  <Box
    data-testid="selected-move-panel"
    sx={{ py: 2, borderBottom: "1px solid rgba(255,255,255,0.12)", minHeight: 98 }}
  >
    <ReviewSectionTitle icon={<CalendarTodayOutlinedIcon />}>Selected move</ReviewSectionTitle>
    {move ? (
      <>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.25 }}>
          <ReviewDataPoint label="Move" value={`${move.moveNumber}. ${move.san}`} />
          <ReviewDataPoint
            label="Clock left"
            value={move.clockSeconds != null ? formatTimeSpent(move.clockSeconds) : "—"}
          />
          <ReviewDataPoint
            label="Think time"
            value={move.timeSpentSeconds != null ? formatTimeSpent(move.timeSpentSeconds) : "—"}
          />
        </Box>
        {revealEvidence &&
          (move.isPlayerMove || showOpponentEvidence) &&
          (move.motifs?.length > 0 ||
            move.tacticalFindings?.length > 0 ||
            (move.principleSignals?.length ?? 0) > 0) && (
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.6, mt: 1.4 }}>
              {(move.motifs.includes("book") ? ["book"] : move.motifs).map((motif) => (
                <Tooltip key={motif} title={motifDescription(motif, move)}>
                  <Box component="span">
                    <StatusBadge label={motifLabel(motif)} />
                  </Box>
                </Tooltip>
              ))}
              {!move.motifs.includes("book") &&
                move.tacticalFindings.map((finding) => (
                  <Tooltip key={`${finding.confidence}:${finding.motif}`} title={tacticalDescription(finding.motif)}>
                    <Box component="span">
                      <StatusBadge
                        label={finding.confidence === "verified" ? titleCase(finding.motif) : "Engine opportunity"}
                      />
                    </Box>
                  </Tooltip>
                ))}
              {!move.motifs.includes("book") &&
                move.principleSignals?.map((signal) => (
                  <PrincipleSignalBadge key={`${signal.principle}:${signal.status}`} signal={signal} />
                ))}
            </Box>
          )}
        {revealEvidence && moment && (
          <Box
            sx={{ mt: 1.5, display: "grid", gap: 1, borderLeft: "3px solid", borderColor: "primary.main", pl: 1.25 }}
          >
            <Box>
              <Typography
                sx={{ color: "text.secondary", fontSize: "0.62rem", fontWeight: 900, letterSpacing: "0.08em" }}
              >
                DECISION
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: "0.78rem", lineHeight: 1.55 }}>
                {moment.decisionQuestion}
              </Typography>
              <Typography sx={{ mt: 0.35, color: "text.secondary", fontSize: "0.7rem", lineHeight: 1.5 }}>
                Candidates ·{" "}
                {moment.candidates
                  .map((candidate) => `${candidate.san}${candidate.source === "played" ? " (played)" : ""}`)
                  .join(" · ")}
              </Typography>
            </Box>
            <Box>
              <Typography
                sx={{ color: "text.secondary", fontSize: "0.62rem", fontWeight: 900, letterSpacing: "0.08em" }}
              >
                CONSEQUENCE
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: "0.78rem", lineHeight: 1.55 }}>{moment.consequence}</Typography>
            </Box>
            {moment.verifiedAlternative && (
              <Box>
                <Typography
                  sx={{ color: "text.secondary", fontSize: "0.62rem", fontWeight: 900, letterSpacing: "0.08em" }}
                >
                  BETTER IDEA
                </Typography>
                <Typography sx={{ mt: 0.25, fontSize: "0.78rem", lineHeight: 1.55 }}>
                  {moment.verifiedAlternative.moves.join(" ")}
                  {moment.verifiedAlternative.finalEvaluation
                    ? ` · ${formatEvaluation(moment.verifiedAlternative.finalEvaluation)}`
                    : ""}
                </Typography>
              </Box>
            )}
            {revealCarry && (
              <Box>
                <Typography
                  sx={{ color: "text.secondary", fontSize: "0.62rem", fontWeight: 900, letterSpacing: "0.08em" }}
                >
                  REMEMBER
                </Typography>
                <Typography sx={{ mt: 0.25, fontSize: "0.78rem", lineHeight: 1.55 }}>{moment.cue}</Typography>
              </Box>
            )}
          </Box>
        )}
      </>
    ) : (
      <Typography sx={{ color: "text.secondary", fontSize: "0.78rem" }}>
        Advance through the game to inspect clock usage and move classifications.
      </Typography>
    )}
  </Box>
);
